export default class Component {

	static _type = 'Component'
	static _last_component_num = 0
	static _after_content_render_listener
	static _before_content_removal_listener

	/**
	 * Anvil engine initialization, do not call this
	 */
	static init () {

		// Create an event listener that will trigger the instantiation of newly inserted components
		Component._after_content_render_listener = (target) => {
			Component.registerWithin(target)
		}

		// Create an event listener that will trigger the destruction of components to be removed
		Component._before_content_removal_listener = (target) => {
			Component.unregisterWithin(target)
		}

		// Subscribe for the proper anvil events
		Anvil.on('after-content-render', Component._after_content_render_listener)
		Anvil.on('before-content-removal', Component._before_content_removal_listener)
	}

	/**
	 * Initialize all components within a DOM element
	 * @param target DOM element to search for components within.
	 */
	static registerWithin (target) {

		let elements = target.querySelectorAll('*[anvil-component]')
		for (let i = 0; i < elements.length; i++) {
			// Note: Unlike Element.attributes, Element.querySelectorAll returns a static NodeList.
			// It's safe to traverse with simple array notation.
			let element = elements[i]

			// The target may contain already initialized and tethered components.
			// If the element has not been tethered yet, do it.
			if (!('anvil' in element)) {

				// Get the component_id
				let component_id = element.getAttribute('anvil-component')
				if (!component_id)
					throw new Error(`Cannot register component. Invalid component id: '${component_id}'`)

				// Initialize the component
				Component.register(component_id)
			}
		}
	}

	/**
	 * Destruct all components within a DOM element
	 * @param target DOM element to search for components within.
	 */
	static unregisterWithin (target) {

		let elements = target.querySelectorAll('*[anvil-component]')
		for (let i = elements.length - 1; i >= 0 ; i--) {
			// Note: Unlike Element.attributes, Element.querySelectorAll returns a static NodeList.
			// It's safe to traverse with simple array notation.
			let element = elements[i]

			// The target may contain not initialized components. We ignore these.
			// If the element has been initialized as a component, destruct it.
			if ('anvil' in element) {
				element.anvil.destroy()
			}
		}
	}

	/**
	 * Register and initialize a single component by it's id.
	 * @param component_id Id of the DOM element we are extending to a component.
	 */
    static register (component_id) {

		// If an id was not passed
        if (typeof component_id == 'undefined')
			throw new Error(`Cannot register component. No component id was passed.`)

		// Check if the component has been instantiated
		if (!(component_id in Anvil.components))
			throw new Error(`Cannot register component. Specified component instance is missing. Id: '${component_id}'`)

		// Component found, grab a reference
		var component = Anvil.components[component_id]

		// At this point, we tether the component to the DOM structure
		component.tether(component_id, true)

		return component
    }

	/**
	 * Generate an id for elements that have no id set explicitly
	 * @param type The type of the element to generate the id for. Mandatory.
	 */
    static generateComponentId (type) {

		if (!type)
			throw new Error(`Cannot generate component id. No component type was specified.`)

        return `${type}-${++Component._last_component_num}`
    }

	/**
	 * Returns the last component offset
	 */
    static getLastComponentNum () {

        return Component._last_component_num
    }

	/**
	 * Find a component in the dom by it's element id
	 * @param selector CSS selector string, preferably an id.
	 */
	static find (selector) {

		try {
			return document.querySelector(selector).anvil
		} catch (e) { }

		return undefined
	}

	/**
	 * Find all components in the dom by their type name
	 * @param type  The type of the component to search for.
	 */
	static findAllOfType (type) {
		return Object.keys(Anvil.components).filter(x => x.startsWith(`${type}-`)).map(x => Anvil.components[x])
	}

	// Instance methods ----------------------------------------------------------------------------

	_id
	_tethered = false
	params = {}

	constructor (params = {}) {

		// Generate component id
		this._id = Component.generateComponentId(this.constructor._type)

		// Add this component to the index
		Anvil.components[this._id] = this

		// Save parameters
		this.params = params
    }

	tether () {

		// Find the DOM element
		let element = document.querySelector(`*[anvil-component="${this._id}"]`)
		if (element === null)
			throw new Error(`Cannot tether component. Cannot find element with id: '${component_id}'`)

		// Save a pair of references, binding the element and the component instance together
		this.base = element
		this.base.anvil = this

		this._tethered = true
	}

    destroy () {

		// Remove the component reference from the DOM element
        this.base.anvil = null
        delete this.base.anvil

		// Remove the DOM element as well (if it has not been removed yet)
		if (this.base.parentNode !== null) this.base.parentNode.removeChild(this.base)
        delete this.base
		this._tethered = false

		// Remove from component index
		delete Anvil.components[this._id]

		//console.log(`Component destroyed: ${this._id}`)
    }

	/*
	 * Returns the component id (typically something like "[ComponentType]-[id]")
	 */
    get id () { return this._id }

	/**
	 * Returns wether the component is "tethered" to it's DOM element or not.
	 * If the component is instantiated before rendering (pre-initialized), tethering will not
	 * happen during instantiation, only after the rendering is completed.
	 *
	 * !!! Important: Pre-initialized components will not read parameters from their respective
	 * DOM elements during instantiation, only after rendering is completed. These components
	 * should use constructor parameters only or wait for tethering.
	 */
	get tethered () { return this._tethered }

	// Helpers -------------------------------------------------------------------------------------

	static groupSearchOpHelper (value, name, form_data, params) {

		// Ignore changes on the checkbox
		if (this.op == name) return

		// Determine if we have any truthy values
		let filled = false
		for (let field of params.fields) {
			if ('name' in field && this.op != field.name) {
				if (Array.isArray(form_data[field.name])) {
					// This is an array value
					if (form_data[field.name].length > 0) {
						filled = true
						break
					}
				} else {
					// Primitive value
					if (form_data[field.name]) {
						filled = true
						break
					}
				}
			}
		}

		if (filled) {
			if (form_data[this.op] == 0) form_data[this.op] = 1
		} else {
			if (form_data[this.op] > 0) form_data[this.op] = 0
		}
	}
}
